import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/vision", label: "The Vision" },
  { to: "/experience", label: "The Experience" },
  { to: "/story", label: "Our Story" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-white/10 bg-rp-bg/80 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-12">
        <Logo />

        <ul className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative font-mono text-xs uppercase tracking-[0.14em] transition-colors duration-200 hover:text-white ${
                    isActive ? "text-rp-accent" : "text-white/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/contact#stay-in-the-loop"
            data-testid="nav-cta"
            className="group hidden items-center gap-2 rounded-full bg-rp-accent px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-[0.12em] text-black transition-colors duration-200 hover:bg-white sm:inline-flex"
          >
            Stay in the Loop
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            onClick={() => setOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white lg:hidden"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div
          className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-rp-surface transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
          data-testid="mobile-menu"
        >
          <div className="flex h-[72px] items-center justify-between px-6">
            <Logo onClick={() => setOpen(false)} />
            <button
              onClick={() => setOpen(false)}
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <ul className="flex flex-col gap-1 px-4 py-6">
            {LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-4 font-display text-2xl tracking-tight transition-colors ${
                      isActive ? "text-rp-accent" : "text-white/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-auto p-6">
            <button
              onClick={() => { setOpen(false); navigate("/contact#stay-in-the-loop"); }}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-rp-accent px-5 py-4 font-mono text-xs font-medium uppercase tracking-[0.12em] text-black"
              data-testid="mobile-nav-cta"
            >
              Stay in the Loop <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
